import React, { useState, useEffect } from 'react';
import {
    Plus,
    Search,
    Trash2,
    GraduationCap,
    AlertCircle,
    Info,
    CheckCircle2,
    ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';
import Modal from '../../components/Common/Modal';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const CollegeDegrees = () => {
    const navigate = useNavigate();
    const [collegeDegrees, setCollegeDegrees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Cascading Dropdown States
    const [levels, setLevels] = useState([]);
    const [domains, setDomains] = useState([]);
    const [degreeNames, setDegreeNames] = useState([]);
    const [specializations, setSpecializations] = useState([]);

    // Form Selection State
    const [selectedLevel, setSelectedLevel] = useState('');
    const [selectedDomain, setSelectedDomain] = useState('');
    const [selectedName, setSelectedName] = useState('');
    const [selectedDegreeId, setSelectedDegreeId] = useState('');
    const [selectedDegreeIds, setSelectedDegreeIds] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchCollegeDegrees();
        fetchLevels();
    }, []);

    const fetchCollegeDegrees = async () => {
        try {
            setLoading(true);
            const { data } = await api.get('/college-degrees');
            if (data.success) {
                setCollegeDegrees(data.data);
            }
        } catch (error) {
            console.error('Error fetching college degrees:', error);
            toast.error('Failed to load your degrees');
        } finally {
            setLoading(false);
        }
    };

    const fetchLevels = async () => {
        try {
            const { data } = await api.get('/degrees/levels');
            if (data.success) {
                setLevels(data.data);
            }
        } catch (error) {
            console.error('Error fetching levels:', error);
        }
    };

    const handleLevelChange = async (level) => {
        setSelectedLevel(level);
        setSelectedDomain('');
        setSelectedName('');
        setSelectedDegreeId('');
        setSelectedDegreeIds([]);
        setDomains([]);
        setDegreeNames([]);
        setSpecializations([]);

        if (!level) return;

        try {
            const { data } = await api.get(`/degrees/domains?level=${encodeURIComponent(level)}`);
            if (data.success) {
                setDomains(data.data);
            }
        } catch (error) {
            console.error('Error fetching domains:', error);
        }
    };

    const handleDomainChange = async (domain) => {
        setSelectedDomain(domain);
        setSelectedName('');
        setSelectedDegreeId('');
        setSelectedDegreeIds([]);
        setDegreeNames([]);
        setSpecializations([]);

        if (!domain) return;

        try {
            const { data } = await api.get(`/degrees/names?level=${encodeURIComponent(selectedLevel)}&domain=${encodeURIComponent(domain)}`);
            if (data.success) {
                setDegreeNames(data.data);
            }
        } catch (error) {
            console.error('Error fetching names:', error);
        }
    };

    const handleNameChange = async (name) => {
        setSelectedName(name);
        setSelectedDegreeId('');
        setSelectedDegreeIds([]);
        setSpecializations([]);

        if (!name) return;

        try {
            const { data } = await api.get(`/degrees/specializations?level=${encodeURIComponent(selectedLevel)}&domain=${encodeURIComponent(selectedDomain)}&fullName=${encodeURIComponent(name)}`);
            if (data.success) {
                setSpecializations(data.data);
            }
        } catch (error) {
            console.error('Error fetching specializations:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const isPG = selectedLevel === 'Postgraduate (PG)';
        let payloadsToRegister = [];

        if (isPG && selectedDegreeIds.length === 0) {
            const generalSpec = specializations.find(s =>
                !s.specialization ||
                s.specialization.toLowerCase() === 'general' ||
                s.specialization.toLowerCase() === 'none'
            );
            if (generalSpec) {
                payloadsToRegister = [{ degreeId: generalSpec._id }];
            } else {
                payloadsToRegister = [{
                    level: selectedLevel,
                    domain: selectedDomain,
                    fullName: selectedName,
                    specialization: 'General',
                    abbreviation: specializations[0]?.abbreviation || '',
                    duration: specializations[0]?.duration || ''
                }];
            }
        } else {
            const ids = isPG ? [...selectedDegreeIds] : (selectedDegreeId ? [selectedDegreeId] : []);
            if (ids.length === 0) {
                toast.error(isPG ? 'Please select at least one specialization.' : 'Please select a specialization');
                return;
            }
            payloadsToRegister = ids.map(id => ({ degreeId: id }));
        }

        try {
            setSubmitting(true);
            const results = await Promise.all(
                payloadsToRegister.map(async (payload) => {
                    try {
                        const res = await api.post('/college-degrees', payload);
                        return { success: true, data: res.data.data };
                    } catch (err) {
                        return { success: false, message: err.response?.data?.message || err.message };
                    }
                })
            );

            const succeeded = results.filter(r => r.success).map(r => r.data);
            const failed = results.filter(r => !r.success);

            if (succeeded.length > 0) {
                toast.success(`${succeeded.length} degree${succeeded.length > 1 ? 's' : ''} added successfully`);
                setCollegeDegrees(prev => [...succeeded, ...prev]);
            }

            if (failed.length > 0) {
                if (succeeded.length === 0) {
                    toast.error(failed[0].message);
                } else {
                    toast.error(`Failed to add some degree(s): ${failed.map(f => f.message).join(', ')}`);
                }
            }

            if (succeeded.length > 0) {
                setIsModalOpen(false);
                resetForm();
            }
        } catch (error) {
            console.error('Error adding degree:', error);
            toast.error('Failed to add degree');
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setSelectedLevel('');
        setSelectedDomain('');
        setSelectedName('');
        setSelectedDegreeId('');
        setSelectedDegreeIds([]);
        setDomains([]);
        setDegreeNames([]);
        setSpecializations([]);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to remove this degree from your college list? This will not delete it from the master list.')) return;
        try {
            const { data } = await api.delete(`/college-degrees/${id}`);
            if (data.success) {
                toast.success('Degree removed successfully');
                setCollegeDegrees(collegeDegrees.filter(d => d._id !== id));
            }
        } catch (error) {
            console.error('Error removing degree:', error);
            toast.error('Failed to remove degree');
        }
    };

    const filteredDegrees = collegeDegrees.filter(degree =>
        degree.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        degree.specialization.toLowerCase().includes(searchTerm.toLowerCase()) ||
        degree.domain.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredDegrees.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredDegrees.length / itemsPerPage);

    return (
        <div className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header Section */}
            <div className="border border-gray-100 dark:border-[#1a3884]/20 rounded-[1.25rem] px-6 py-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-[#002147]">
                <div className="flex items-center gap-4 text-left">
                    <button onClick={() => navigate('/dashboard')} className="p-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-500 transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="space-y-0.5 text-left">
                        <h1 className="text-[22px] font-black text-[#1a3884] dark:text-[#4f7dff] tracking-tight">
                            Degree Management
                        </h1>
                        <div className="flex items-center gap-1.5 text-[13px] font-medium text-gray-500 dark:text-gray-400">
                            <GraduationCap className="w-4 h-4 text-[#1a3884]/60 dark:text-[#4f7dff]/60" />
                            Configure and manage the academic degrees offered by your institution
                        </div>
                    </div>
                </div>
                <Button 
                    variant="primary" 
                    icon={Plus} 
                    onClick={() => setIsModalOpen(true)}
                    className="border-none bg-[#1a3884] hover:bg-[#132A55] text-white rounded-xl shadow-md px-5 py-2.5"
                >
                    Add New Degree
                </Button>
            </div>

            <Card className="overflow-hidden border-none shadow-sm ring-1 ring-gray-200">
                <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search your degrees by name, domain, or specialization..."
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 translate-y-0 opacity-100 transition-all duration-500">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
                        <p className="text-gray-500 font-medium">Loading your degree configurations...</p>
                    </div>
                ) : filteredDegrees.length === 0 ? (
                    <div className="text-center py-20 bg-white">
                        <div className="bg-gray-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-100">
                            <GraduationCap className="w-10 h-10 text-gray-300" />
                        </div>
                        <p className="text-gray-900 font-semibold text-lg">No degrees registered yet</p>
                        <p className="text-gray-500 mt-2 max-w-sm mx-auto">Click the button above to start adding academic programs offered by your institution.</p>
                        <Button 
                            variant="outline" 
                            className="mt-6"
                            onClick={() => setIsModalOpen(true)}
                        >
                            Get Started
                        </Button>
                    </div>
                ) : (
                    <div className="bg-white">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-gray-50/50 border-b border-gray-100">
                                    <tr>
                                        <th className="py-4 px-6 text-xs font-bold text-gray-600 uppercase tracking-wider">Degree Level & Domain</th>
                                        <th className="py-4 px-6 text-xs font-bold text-gray-600 uppercase tracking-wider">Full Name & Abbreviation</th>
                                        <th className="py-4 px-6 text-xs font-bold text-gray-600 uppercase tracking-wider">Specialization</th>
                                        <th className="py-4 px-6 text-xs font-bold text-gray-600 uppercase tracking-wider text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {currentItems.map((degree) => (
                                        <tr key={degree._id} className="hover:bg-gray-50/80 transition-all duration-200">
                                            <td className="py-4 px-6">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-semibold text-gray-900">{degree.level}</span>
                                                    <span className="text-xs text-gray-500 font-medium">{degree.domain}</span>
                                                </div>
                                            </td>
                                            <td className="py-4 px-6">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-bold text-gray-800">{degree.fullName}</span>
                                                    <span className="text-xs inline-flex items-center px-2 py-0.5 mt-1 rounded text-primary-700 bg-primary-50 w-fit font-bold">
                                                        {degree.abbreviation}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-4 px-6">
                                                <span className="text-sm text-gray-600 font-medium">
                                                    {degree.specialization}
                                                </span>
                                            </td>

                                            <td className="py-4 px-6 text-right">
                                                <button
                                                    onClick={() => handleDelete(degree._id)}
                                                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                                    title="Remove from college list"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="p-4 flex items-center justify-between bg-gray-50/30 border-t border-gray-100">
                                <div className="text-sm text-gray-500">
                                    Showing <span className="font-semibold text-gray-700">{indexOfFirstItem + 1}</span> to <span className="font-semibold text-gray-700">{Math.min(indexOfLastItem, filteredDegrees.length)}</span> of <span className="font-semibold text-gray-700">{filteredDegrees.length}</span> results
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                        className="h-9 px-4"
                                    >
                                        Previous
                                    </Button>
                                    <div className="px-3 py-1.5 text-sm font-bold text-gray-700 bg-white border border-gray-200 rounded-lg shadow-sm">
                                        Page {currentPage} of {totalPages}
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                        className="h-9 px-4"
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Card>

            <Modal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    resetForm();
                }}
                title="Add New Academic Program"
                className="max-w-2xl"
            >
                <div className="mb-6 p-4 bg-primary-50 rounded-xl border border-primary-100 flex items-start gap-3">
                    <Info className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <div>
                        <p className="text-sm text-primary-800 font-medium">Adding degree from master list</p>
                        <p className="text-xs text-primary-600 mt-1">Select the level, domain, and degree name to find your specific offering. All data is fetched from the national academic standard database.</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Level Selector */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1.5 uppercase tracking-wide">1. Degree Level</label>
                        <select
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none appearance-none bg-white cursor-pointer"
                            value={selectedLevel}
                            onChange={(e) => handleLevelChange(e.target.value)}
                            required
                        >
                            <option value="">Select Level</option>
                            {levels.map(lvl => (
                                <option key={lvl} value={lvl}>{lvl}</option>
                            ))}
                        </select>
                    </div>

                    {/* Domain Selector */}
                    <div>
                        <label className={`block text-sm font-bold mb-1.5 uppercase tracking-wide ${!selectedLevel ? 'text-gray-400' : 'text-gray-700'}`}>
                            2. Academic Domain
                        </label>
                        <select
                            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none appearance-none bg-white cursor-pointer ${!selectedLevel ? 'border-gray-200 bg-gray-50 text-gray-400 grayscale cursor-not-allowed' : 'border-gray-300'}`}
                            value={selectedDomain}
                            onChange={(e) => handleDomainChange(e.target.value)}
                            required
                            disabled={!selectedLevel}
                        >
                            <option value="">Select Domain</option>
                            {domains.map(dom => (
                                <option key={dom} value={dom}>{dom}</option>
                            ))}
                        </select>
                    </div>

                    {/* Name Selector */}
                    <div>
                        <label className={`block text-sm font-bold mb-1.5 uppercase tracking-wide ${!selectedDomain ? 'text-gray-400' : 'text-gray-700'}`}>
                            3. Degree Full Name
                        </label>
                        <select
                            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none appearance-none bg-white cursor-pointer ${!selectedDomain ? 'border-gray-200 bg-gray-50 text-gray-400 grayscale cursor-not-allowed' : 'border-gray-300'}`}
                            value={selectedName}
                            onChange={(e) => handleNameChange(e.target.value)}
                            required
                            disabled={!selectedDomain}
                        >
                            <option value="">Select Degree Name</option>
                            {degreeNames.map(name => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Specialization Selector */}
                    <div>
                        <label className={`block text-sm font-bold mb-1.5 uppercase tracking-wide ${!selectedName ? 'text-gray-400' : 'text-gray-700'}`}>
                            {selectedLevel === 'Postgraduate (PG)' 
                                ? '4. Specialization(s) (Optional - Select multiple)' 
                                : '4. Specialization'
                            }
                        </label>
                        {selectedLevel === 'Postgraduate (PG)' ? (
                            <div className={`space-y-2 max-h-48 overflow-y-auto border rounded-xl p-3 bg-white ${!selectedName ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed' : 'border-gray-300'}`}>
                                {specializations.length === 0 ? (
                                    <p className="text-sm text-gray-500 text-center py-4">No specializations available</p>
                                ) : (
                                    specializations.map(spec => {
                                        const isChecked = selectedDegreeIds.includes(spec._id);
                                        return (
                                            <label key={spec._id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    disabled={!selectedName}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedDegreeIds([...selectedDegreeIds, spec._id]);
                                                        } else {
                                                            setSelectedDegreeIds(selectedDegreeIds.filter(id => id !== spec._id));
                                                        }
                                                    }}
                                                    className="mt-1 rounded border-gray-300 text-[#1a3884] focus:ring-[#1a3884]"
                                                />
                                                <div className="text-left">
                                                    <p className="text-sm font-semibold text-gray-800">
                                                        {spec.specialization} {spec.abbreviation ? `(${spec.abbreviation})` : ''}
                                                    </p>
                                                    <p className="text-xs text-gray-500">{spec.duration} duration</p>
                                                </div>
                                            </label>
                                        );
                                    })
                                )}
                            </div>
                        ) : (
                            <select
                                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none appearance-none bg-white cursor-pointer ${!selectedName ? 'border-gray-200 bg-gray-50 text-gray-400 grayscale cursor-not-allowed' : 'border-gray-300'}`}
                                value={selectedDegreeId}
                                onChange={(e) => setSelectedDegreeId(e.target.value)}
                                required
                                disabled={!selectedName}
                            >
                                <option value="">Select Specialization</option>
                                {specializations.map(spec => (
                                    <option key={spec._id} value={spec._id}>
                                        {spec.specialization} {spec.abbreviation ? `(${spec.abbreviation})` : ''} - {spec.duration}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Preview Area */}
                    {((selectedLevel !== 'Postgraduate (PG)' && selectedDegreeId) || 
                      (selectedLevel === 'Postgraduate (PG)' && selectedDegreeIds.length > 0)) && (
                        <div className="p-4 bg-green-50 rounded-xl border border-green-100 flex items-center justify-between animate-in fade-in zoom-in duration-300">
                            <div className="flex items-center gap-3">
                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                                <span className="text-sm text-green-800 font-semibold">
                                    {selectedLevel === 'Postgraduate (PG)' 
                                        ? `${selectedDegreeIds.length} Specialization(s) Selected` 
                                        : "Degree Selected"
                                    }
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-100">
                        <Button 
                            variant="outline" 
                            type="button" 
                            onClick={() => {
                                setIsModalOpen(false);
                                resetForm();
                            }}
                            className="h-11 px-6"
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="primary" 
                            type="submit" 
                            loading={submitting}
                            disabled={submitting || (!selectedLevel || !selectedName || (selectedLevel !== 'Postgraduate (PG)' && !selectedDegreeId))}
                            className="h-11 px-8 shadow-md"
                        >
                            Register Degree
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default CollegeDegrees;
